import { QueryResult } from '../../../../hooks/store';
import { normalizedPagination } from '../../../../utils';
import { PageManagerPageType } from './page-manager';
import { PagedRow, PageManagerBase, RowFilterCallback, RowIteratorCallback } from './page-manager-base';

export class PageManagerRtk<RowType> extends PageManagerBase<RowType> {
    constructor() {
        super();
        this.pages = [];
    }

    private isQueryResult(page: PageManagerPageType<RowType>): page is QueryResult<RowType[]> {
        return !!page?.hasOwnProperty('isLoading');
    }

    canAddPage(page: PageManagerPageType<RowType>) {
        if (this.isQueryResult(page)) {
            return this.pageHasData(page?.data);
        }

        return false;
    }

    addPage(page: PageManagerPageType<RowType>): void {
        if (this.isQueryResult(page)) {
            const pageNumber = this.validateNewPage(page.data, this.pages.length);

            if (pageNumber !== -1) {
                this.pages[pageNumber] = page;
            }
        }

        this.updateLength();
    }

    isEmptyPage(page: PageManagerPageType<RowType>) {
        if (this.isQueryResult(page)) {
            return !this.pageHasData(page?.data);
        }

        return false;
    }

    removePage(page: PageManagerPageType<RowType>): void {
        const pageNumber = this.isQueryResult(page) ? normalizedPagination(page.data.pagination).currentPage : 0;

        if (pageNumber > 0) {
            delete this.pages[pageNumber];
        }
    }

    getPage(pageNumber: number): PageManagerPageType<RowType> {
        return this.pages[pageNumber];
    }

    map<ReturnType>(callback: RowIteratorCallback<RowType, ReturnType>): ReturnType[] {
        let rowIndex = 0;

        const rawPages = this.pages.slice(this.startingPage);

        return rawPages
            .map((page) => {
                if (!page) {
                    return [];
                }

                if (this.isQueryResult(page)) {
                    if (page.isLoading || page.isFetching) {
                        return [];
                    }

                    return this.mapPageRows(callback, () => rowIndex++, page?.data);
                }

                throw new Error('Unexpected type mismatch on mapping page');
            })
            .flat();
    }

    filter(callback: RowFilterCallback<RowType>): PagedRow<RowType>[] {
        const rawPages = this.pages.slice(this.startingPage);
        const resultPages: PagedRow<RowType>[] = [];

        for (const page of rawPages) {
            if (this.isQueryResult(page)) {
                if (!page) {
                    continue;
                }

                if (page.isLoading || page.isFetching) {
                    continue;
                }

                this.filterPageRows(callback, page?.data, resultPages);
            } else {
                throw new Error('Unexpected type mismatch on filtering page');
            }
        }

        return resultPages;
    }
}
